'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './AttachmentChip.module.css';

export type AttachmentChipStatus = 'ready' | 'uploading' | 'error';

export interface AttachmentChipProps extends React.HTMLAttributes<HTMLDivElement> {
  /** File name shown on the first line. */
  filename: string;
  /** IANA media type — drives icon + categorisation. */
  mediaType: string;
  /** Size in bytes (formatted to KB/MB for display). */
  size: number;
  /** Lifecycle. `error` shows the danger styling and error text. */
  status?: AttachmentChipStatus;
  /** Optional thumbnail URL — typical for image attachments. */
  thumbnailUrl?: string | null;
  /** Error string surfaced when `status === 'error'`. */
  error?: string;
  /** When set, renders an `×` button. */
  onRemove?: () => void;
  /** Override the auto-derived secondary line. */
  metaText?: string;
  /** Override the inferred icon. */
  icon?: React.ReactNode;
  /** Accessible label fallback for the chip itself. */
  ariaLabel?: string;
  className?: string;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14" aria-hidden>
      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
    </svg>
  );
}

/**
 * Default icon used when consumers don't pass a custom one. Kept as inline
 * SVG so the design system stays free of `@mui/icons-material` coupling.
 */
function DefaultFileIcon({ mediaType }: { mediaType: string }) {
  if (mediaType.startsWith('image/')) {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    );
  }
  if (mediaType === 'application/pdf') {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
      </svg>
    );
  }
  if (
    mediaType === 'application/msword' ||
    mediaType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M9 13l1.5 4 1.5-3 1.5 3L15 13" />
      </svg>
    );
  }
  if (mediaType.startsWith('text/') || mediaType === 'application/json') {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    );
  }
  // Generic paperclip
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

/**
 * AttachmentChip
 *
 * Pill-style file chip used inside chat input trays.
 *
 * - Renders an image thumbnail when `thumbnailUrl` is provided.
 * - Otherwise shows an inferred file-type icon.
 * - During `uploading`, shows a shimmer bar; during `error`, switches to
 *   the danger color set and displays the error string.
 */
export const AttachmentChip = React.forwardRef<HTMLDivElement, AttachmentChipProps>(
  (
    {
      filename,
      mediaType,
      size,
      status = 'ready',
      thumbnailUrl,
      error,
      onRemove,
      metaText,
      icon,
      ariaLabel,
      className,
      ...rest
    },
    ref
  ) => {
    const isImage = mediaType.startsWith('image/');
    const showThumbnail = isImage && !!thumbnailUrl;
    const meta =
      status === 'error'
        ? error ?? 'Upload failed'
        : status === 'uploading'
          ? 'Uploading…'
          : metaText ?? formatBytes(size);

    return (
      <div
        ref={ref}
        role="listitem"
        aria-label={ariaLabel ?? `${filename}, ${mediaType}`}
        data-state={status}
        data-image={showThumbnail ? 'true' : undefined}
        className={clsx(styles.chip, className)}
        {...rest}
      >
        {showThumbnail ? (
          <img src={thumbnailUrl!} alt="" className={styles.thumb} />
        ) : (
          <span className={styles.iconWrap} data-state={status} aria-hidden>
            {status === 'error' ? <ErrorIcon /> : (icon ?? <DefaultFileIcon mediaType={mediaType} />)}
          </span>
        )}

        <div className={styles.body}>
          <span className={styles.filename} title={filename}>
            {filename}
          </span>
          <span className={styles.meta}>{meta}</span>
        </div>

        {onRemove && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={onRemove}
            aria-label={`Remove ${filename}`}
          >
            <CloseIcon />
          </button>
        )}

        {status === 'uploading' && <span aria-hidden className={styles.shimmer} />}
      </div>
    );
  }
);

AttachmentChip.displayName = 'AttachmentChip';

export default AttachmentChip;
